// Reporting boards 2, 4 and 5 (Marius, 1 Nov 2026): a report's "Planned and
// actual" tab, the checks before it is sent ("Check and submit") and the
// report as the funder receives it ("Preview"). Owned by WP admin.
import { z } from "zod";
import { LIMITS } from "../limits";
import { uuid } from "../schemas/common";
import { textOrEmpty, type Id, type StatusChip } from "./common";

// ---------- board 2: planned against actual ----------

/** One activity of the period: what the workplan said against what the record shows. */
export type PlannedActualRow = {
  activityId: Id;
  code: string;
  title: string;
  path: string;
  /** "Mar" against "11–12 Mar"; "—" when there is none; "late" for work past its planned end and not done. */
  when: { plan: string; actual: string };
  /** Participants planned against attended (events); "—" for other work. */
  people: { plan: string; actual: string };
  /** The planned cost (the activity's estimate, else its ToR) against what is spent on it. null = none. */
  cost: { planCents: number | null; actualCents: number | null };
  /** As planned, Different, Not planned. */
  status: StatusChip;
  /** "Different" and "Not planned" need a reason for the funder. */
  needsReason: boolean;
  /** The team's reason, kept with the report. */
  reason: string;
};

/** One indicator: where it should be by the end of the period (the plan mark) and where it is. */
export type PlannedIndicatorRow = {
  id: Id;
  code: string;
  name: string;
  /** "3", "30%"; "" when the indicator has no target. */
  plan: string;
  /** "4", "35%"; "No reading yet". */
  now: string;
  /** Where plan and now sit on the bar, 0 to 100 (of the target). */
  planPct: number;
  nowPct: number;
  /** Ahead, On plan, Behind; "No reading" when there is none. */
  status: StatusChip;
};

/** One budget heading: planned for the period against spent in it. */
export type PlannedBudgetRow = { heading: string; plannedCents: number; spentCents: number; /** spent against planned, rounded; null when nothing was planned. */ diffPct: number | null };

/** GET /reports/:id/planned (board 2). */
export type PlannedActualView = {
  reportId: Id;
  title: string;
  /** "March to August 2026" */
  period: string;
  /** "August": the month the indicators' plan marks are for. */
  periodEndMonth: string;
  figures: {
    planned: number;
    asPlanned: number;
    different: number;
    explained: number;
    notPlanned: number;
    plannedCents: number;
    spentCents: number;
    /** spent as a share of the plan; null when nothing was planned. */
    spentPct: number | null;
  };
  activities: PlannedActualRow[];
  indicators: PlannedIndicatorRow[];
  budget: PlannedBudgetRow[];
  /** The team can change the reasons (a draft, or sent back, and a role that edits). */
  canWrite: boolean;
};

/** PUT /reports/:id/planned/reasons: the reason one activity differs from the plan. */
export const plannedReasonBody = z.object({ activityId: uuid, reason: textOrEmpty(LIMITS.reasonText, "The reason") });
export type PlannedReasonBody = z.infer<typeof plannedReasonBody>;

// ---------- board 4: check and submit ----------

/** One check between the report's text and the record, before it is sent. */
export type ReportCheckRow = {
  key: string;
  kind: "figures_differ" | "changed" | "proof_missing" | "not_explained" | "consent" | "blocker";
  /** "Figures differ", "Changed since last report", "Proof missing", "Not explained", "Consent", "Blocker". */
  tag: StatusChip;
  /** fix: to fix before sending; look: to look at. */
  severity: "fix" | "look";
  text: string;
  /** "Results · A2.1 to A2.3", "Money · Financial report". */
  where: string;
  /** A link ("See both", "Explain", "Add proof"), or "Use 76": the record's figure put into the section's text (POST /reports/:id/sections/:key/use). */
  action: { label: string; path: string } | { label: string; use: { section: string; find: string; replace: string } } | null;
};

/**
 * The funder's template (board 4 "The funder's template"): each part of the EU
 * narrative report (Annex VI), the report section that fills it (by key) and
 * what fills it. The output keeps the funder's numbering.
 */
export const EU_ANNEX_VI_PARTS: { part: string; section: string; fills: string }[] = [
  { part: "1. Description of the action", section: "context", fills: "Contract details, logframe summary" },
  { part: "2. Summary of the period", section: "summary", fills: "Key figures, highlights" },
  { part: "3. Results achieved", section: "results", fills: "Indicator charts, proof, saved quotes" },
  { part: "4. Activities implemented", section: "activities", fills: "Planned against actual, explanations" },
  { part: "5. Target groups reached", section: "reach", fills: "Analytics: countries, gender, sectors, returning" },
  { part: "6. Cooperation with partners", section: "reach", fills: "Network, organisations" },
  { part: "7. Visibility", section: "visibility", fills: "Publications, branded events, press" },
  { part: "8. Difficulties and changes", section: "difficulties", fills: "Change timeline, assumptions, lessons" },
  { part: "9. Financial overview", section: "money", fills: "Financial report, cost per participant, forecast" },
  { part: "10. Work plan next period", section: "next_period", fills: "Workplan of the next period" },
  { part: "Annexes", section: "annexes", fills: "Numbered evidence bundle" },
];
/** The final report: "Lessons and sustainability" in place of the next period's work plan. */
export const EU_ANNEX_VI_FINAL_PARTS = EU_ANNEX_VI_PARTS.map((x) => (x.section === "next_period" ? { part: "10. Lessons and sustainability", section: "lessons", fills: "What the team learned, what lasts" } : x));

/** GET /reports/:id/submit-checks (board 4): the checks that are not per section, and what goes out. */
export type ReportChecksView = {
  reportId: Id;
  title: string;
  checks: ReportCheckRow[];
  /** "EU narrative report (Annex VI)" */
  template: string;
  /** The funder's parts in order; null for the team's own template (its sections are its parts). */
  parts: { part: string; section: string; fills: string }[] | null;
  /** Where the template is chosen (How this project works). */
  templatePath: string;
  /** "43 files", "41 of 43 activities with proof". */
  annexes: { files: number; line: string };
  previewPath: string;
  dataPath: string;
};

/** POST /reports/:id/sections/:key/use: put the record's figure into the section's text in place of the one written. */
export const useFigureBody = z.object({ find: z.string().min(1).max(200), replace: z.string().min(1).max(200) });
export type UseFigureBody = z.infer<typeof useFigureBody>;

// ---------- board 5: the report as the funder receives it ----------

/** GET /reports/:id/preview (board 5): what goes around the sections on the A4 pages. The sections and their cards are the report builder's (GET /reports/:id/write). */
export type ReportPreviewView = {
  reportId: Id;
  /** "Interim narrative report 2", "Final narrative report" */
  kicker: string;
  project: string;
  /** "Reporting period 1 March to 31 August 2026" */
  period: string;
  /** The cover's details: Contract, Contracting authority, Implemented by, Partner countries, Submitted. */
  details: { label: string; value: string }[];
  orgName: string;
  /** The organisation's logo, when the project has one. */
  logoUrl: string | null;
  /** The EU emblem and "Funded by the European Union" (the project shows the EU statement). */
  euStatement: boolean;
  /** The small print at the foot of the cover (the Kosovo designation, the EU disclaimer); null = none. */
  footnote: string | null;
  /** The line at the top of every page after the cover: "Project · Interim narrative report 2 · 1 March to 31 August 2026". */
  runningHead: string;
  reference: string;
  /** The line at the foot of every page: "Prepared with Convenor from the project record. Figures are linked to their source until submission." */
  footer: string;
  /** The activities of the period, planned against actual, with the reasons (the "Activities" section). */
  activities: PlannedActualRow[];
  /** The numbered annexes (the "Annexes" section and the annex bundle's index). */
  annexes: { number: number; title: string }[];
  /** The Word file, made from the same sections and cards. */
  docxPath: string;
  sent: boolean;
};
